import { leases as leaseApi } from '@/api/endpoints'
import type { Lease } from '@/api/types'
import { Section } from '@/components/layout/Page'
import { changeText } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { leaseChanged } from '@/hooks/usePage'
import { usePoll } from '@/hooks/usePoll'
import { useRefreshAfterWrites } from '@/hooks/useWorld'

// 변경 대기. 관리자에게는 검토 대기가 없으므로 자기가 관리하는 팀의 신청이 VM 목록 위에 선다 —
// 한 줄이 한 신청이고 보기만이다: 승인·반려는 최고 관리자가 검토 대기에서 한다.
// 관리자는 자기 팀 사람의 VM 을 보고 들어가기만 하므로(`domain/lease.ts` 의 `mayChange`) 칩이 서지 않는다.
// 목록의 쪽과 따로, 열린 변경이 있는 임대만 서버에서 읽는다(`pending_change=true`) — 쪽에 없는 VM 의 신청도 선다.
// 변경은 반납·보존과 함께 닫혀 서버가 쓰는 중인 VM 에만 싣는다 — 받은 임대를 상태로 다시 거르지 않는다.
// 승인된 뒤 적용을 기다리는 변경도 함께 오므로 승인을 기다리는 것만 줄로 세운다.

// 목록과 같은 간격이다. 관리자는 스트림이 없어 이 간격과 쓰기 뒤의 다시 읽기가 전부다.
const POLL_MS = 8000

export function PendingChanges() {
  const read = usePoll(() => leaseApi.list({ pending_change: true }), useFallback(POLL_MS))
  useRefetchOn(leaseChanged, read.refresh, 1000)
  useRefreshAfterWrites(read.refresh)
  const rows = (read.data ?? []).filter((l) => l.pending_change?.state === 'pending_approval')
  if (rows.length === 0) return null
  return (
    <Section title={`변경 대기 ${rows.length}`}>
      <ul className="divide-y overflow-hidden rounded-lg border bg-card">
        {rows.map((l) => (
          <ChangeRow key={l.id} lease={l} />
        ))}
      </ul>
    </Section>
  )
}

function ChangeRow({ lease: l }: { lease: Lease }) {
  const change = l.pending_change
  if (!change) return null
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]">
      <span className="font-medium">{l.name}</span>
      <span className="text-muted-foreground">{ownerLabel(l.owner)}</span>
      <span className="flex-1 truncate text-muted-foreground max-sm:basis-full max-sm:whitespace-normal">
        {changeText(change, l.gpus)}
        {change.reason ? ` — ${change.reason}` : ''}
      </span>
    </li>
  )
}
